<template>
  <Form as="v-form" @submit="validateForm" :validation-schema="formSchema" v-slot="{ errors }">

      <!-- Loading data spinner -->
      <v-col class="pa-0 text-center">
        <scale-loading-spinner
          v-if="loadingData"
          text="Loading data..."
          size="small"
        ></scale-loading-spinner>
      </v-col>

      <!-- FORM FIELDS -->
      <v-col
        v-for="(formField, index) in formFields"
        :key="index"
        :class="getFieldPadding(formField)"
        cols="12"
        :order="index"
      >

        <!-- Text fields -->
        <template v-if="formField.name !== 'country'">
          <!-- readonly text fields -->
          <VTextField
            v-if="formField.readonly"
            :value="personalData[formField.name]"
            :label="t('formFields.' + formField.name)"
            :readonly="formField.readonly"
            aria-labelledby="heading"
          >
            <div
              slot="tooltip-info"
              v-if="formField.name === 'name'"
              class="tooltip d-flex justify-end"
            >
              <scale-tooltip
                :content="t('text.nameTooltipInfo')"
                placement="bottom-end"
              >
                <scale-icon-alert-information
                  class="tooltip-icon"
                  accessibility-title="information"
                  fill="grey"
                />
              </scale-tooltip>
            </div>
          </VTextField>

          <!-- editable text fields -->
          <Field
            v-else
            v-model="personalData[formField.name]"
            :name="formField.name"
            v-slot="{ field }"
            validateOnInput
            :rules="formSchema[formField.name]"
          >
            <VTextField
              v-bind="field"
              @input="isFormModified = true"
              :label="t('formFields.' + formField.name) + ' *'"
              :helper-text="errors[formField.name]"
              :invalid="errors.hasOwnProperty(`${ formField.name }`)"
              :readonly="formField.readonly"
              :max-length="formField.maxLength"
              :counter="personalData[formField.name].length > formField.maxLength - 5"
              aria-labelledby="heading"
            >
            </VTextField>
          </Field>
        </template>

        <!-- Dropdown -->
        <scale-dropdown
          v-else
          :label="t('formFields.' + formField.name)"
          @scaleChange="handleCountryChange($event, formField)"
          :helper-text="formField.errorMsg"
          :invalid="formField.errorMsg !== ''"
          aria-labelledby="heading"
        >
          <option
            :value="undefined"
            :selected="personalData[formField.name] === ''"
          ></option>
          <option
            v-for="(country, index) in countries"
            :key="index"
            :value="country.value"
            :selected="country.value === personalData[formField.name]"
          >
            {{ country.label }}
          </option>
        </scale-dropdown>
      </v-col>

      <!-- Form action buttons -->
      <v-row
        class="ma-0 d-flex"
        :justify="isFormModified ? 'space-between' : 'end'"
      >
        <scale-button
          id="secondaryBtn"
          type="button"
          class="mr-2"
          @click="isFormModified ? cancelEditing() : changeScreen('emailScreen')"
          variant="informational"
        >
          {{
            t("text.mainScreen.secondaryBtn" + (isFormModified ? "2" : "1"))
          }}
        </scale-button>
        
        <scale-button
          id="primaryBtn"
          type="submit"
          :disabled="!isFormModified"
        >
          {{ t("text.mainScreen.primaryBtn") }}
        </scale-button>
      </v-row>
  </Form>
</template>

<script setup>
import { ref, reactive, computed, onMounted, defineEmits, inject } from 'vue';

// Components
import VTextField from "./VTextField.vue";
import PersonAPI from "../services/PersonAPI";

// Plugins
import { useI18n } from 'vue-i18n'
import { Form, Field } from 'vee-validate';
import isoCountries from '@/plugins/vue-i18n';


// Emits
const emit = defineEmits(['screen-change', 'data-updated', 'notify'])


// Reusable data
const keycloak = inject('keycloak')
const { t, locale } = useI18n() 

const personalData = reactive({
  title: "",
  name: "",
  pernr: "",
  birthdate: "",
  street: "",
  city: "",
  zip: "",
  country: "DE",
  phoneNbr: "",
  email: "",
});

const formFields = ref([
  { name: "title", readonly: true },
  { name: "name", readonly: true },
  { name: "pernr", readonly: true },
  { name: "birthdate", readonly: true },
  {
    name: "street",
    maxLength: 60,
    readonly: false,
    rules: "required|max:60",
  },
  {
    name: "city",
    maxLength: 40,
    readonly: false,
    rules: "required|max:40",
  },
  {
    name: "zip",
    maxLength: undefined,
    readonly: false,
    rules: `required|DE_zip:`,
  },
  {
    name: "country",
    errorMsg: "",
    readonly: false,
  },
  {
    name: "phoneNbr",
    maxLength: 14,
    readonly: false,
    rules: "required|max:14|phone",
  },
  { name: "email", readonly: true },
]);

const isFormValid = ref(true);
const isFormModified = ref(false);
const loadingData = ref(false);


// Validation
const formSchema = computed(() => {
  const schema = {};

  formFields.value.forEach(field => {
    if (field.rules && !field.readonly) {
      if(field.name === "zip") {
        schema[field.name] = field.rules + personalData.country;
      } else {
        schema[field.name] = field.rules;
      }
    }
  });

  return schema;
})

const validateForm = () => {
  isFormValid.value = false;
  
  let isCountryValid = formFields.value[7].value !== "";
  if (isCountryValid) {
      isFormValid.value = true;
      if (isFormModified.value) {
        updatePersonalData();
      } else {
        cancelEditing();
      }
    }
};

const resetValidation = () => {
  getUserData();
};

// Computed properties
const countries = computed(() => {
  const list = isoCountries.getNames("en", {
    select: "official",
  });
  return Object.keys(list).map((key) => ({ value: key, label: list[key] }));
});


// Methods
const getFieldPadding = (field) => {
  if (field.readonly) {
    return "pa-0 pb-8";
  } else if (personalData[field.name].length > field.maxLength - 5) {
    return "pa-0 pb-3";
  } else {
    return "pa-0 pb-8";
  }
};

const changeScreen = (screenName) => {
  emit("screen-change", screenName);
};

const handleCountryChange = ($event, field) => {
  if ($event.target.value === "DE") {
    formFields.value[6].maxLength = 5;
  } else {
    formFields.value[6].maxLength = undefined;
  }
  personalData[field.name] = $event.target.value;
  field.errorMsg = "";
  isFormModified.value = true;
};

const cancelEditing = () => {
  isFormModified.value = false;
  resetValidation();
};

// API methods
const getUserData = () => {
  loadingData.value = true;
  PersonAPI.getPersonalData(keycloak.token)
    .then((res) => {
      Object.assign(personalData, {
        title: res.data.title,
        name: res.data.firstName + " " + res.data.lastName,
        pernr: res.data.pernr,
        birthdate: res.data.birthdate,
        street: res.data.street,
        city: res.data.city,
        zip: res.data.zip,
        country: res.data.country,
        phoneNbr: res.data.phoneNbr,
        email: res.data.email,
      });
      loadingData.value = false;
    })
    .catch((err) => {
      loadingData.value = false;
      console.error("Error getting data -> ", err);
      emit("notify", "danger",
        t("notificationMsg.getData.error"),
        err.response.errors)
    });
};

const updatePersonalData = () => {
  if (personalData.country.length > 2) {
    personalData.country = isoCountries.getAlpha2Code(
      personalData.country,
      locale.value
    );
  }
  PersonAPI.updateData(keycloak.token, personalData)
    .then((res) => {
      emit("data-updated", res.data.sessionId);
    })
    .catch((err) => {
      console.error("Error updating data -> ", err.response);
      emit("notify", "danger",
        t("notificationMsg.getData.error"),
        err.response.errors)
    });
};

// Lifecycle hooks
onMounted(() => {
  getUserData();
});
</script>

<style lang="scss" scoped>
.input-name {
  position: relative;
}
.tooltip {
  position: absolute;
  z-index: 3;
  right: 12px;
  top: 12px;
  width: 50vw;
  max-width: 300px;
}
.tooltip-icon:hover {
  cursor: pointer;
  path {
    fill: #b8b8ba !important;
  }
}
</style>
